import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  GraduationCap, Users, Trophy, Target, Code, Briefcase, TrendingUp,
  LogOut, BarChart3, Award, Star, ChevronRight, Loader, Search,
  Medal, Activity, Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CollegeDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [students, setStudents] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentsPage, setStudentsPage] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);

  const token = localStorage.getItem('college_token');
  const collegeData = JSON.parse(localStorage.getItem('college_data') || '{}');

  useEffect(() => {
    if (!token) {
      navigate('/college/auth');
      return;
    }
    fetchDashboard();
  }, [token, navigate]);

  useEffect(() => {
    if (activeTab === 'students') {
      fetchStudents();
    } else if (activeTab === 'leaderboard') {
      fetchLeaderboard();
    } else if (activeTab === 'analytics') {
      fetchAnalytics();
    }
  }, [activeTab, studentsPage, studentSearch]);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/api/college/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(res.data);
    } catch (err) {
      toast.error('Failed to load dashboard');
      if (err.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const res = await axios.get(`${API}/api/college/students`, {
        params: { page: studentsPage, search: studentSearch || undefined },
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(res.data.students || []);
      setTotalStudents(res.data.total || 0);
    } catch (err) {
      console.error('Failed to load students');
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/api/college/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(res.data.leaderboard || []);
    } catch (err) {
      console.error('Failed to load leaderboard');
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/api/college/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to load analytics');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('college_token');
    localStorage.removeItem('college_data');
    navigate('/college/auth');
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg">1</div>;
    if (rank === 2) return <div className="w-8 h-8 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full flex items-center justify-center text-white font-bold shadow">2</div>;
    if (rank === 3) return <div className="w-8 h-8 bg-gradient-to-br from-amber-600 to-orange-700 rounded-full flex items-center justify-center text-white font-bold shadow">3</div>;
    return <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 font-medium">{rank}</div>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="animate-spin text-purple-600" size={32} />
      </div>
    );
  }

  const stats = dashboard?.stats || {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <GraduationCap size={24} />
              </div>
              <div>
                <h1 className="font-bold">{collegeData.college_name || 'College Dashboard'}</h1>
                <p className="text-xs text-white/70">{collegeData.location}</p>
              </div>
            </div>
            
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 -mb-px">
            {['overview', 'students', 'leaderboard', 'analytics'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 font-medium text-sm transition-all border-b-2 ${
                  activeTab === tab
                    ? 'border-white text-white'
                    : 'border-transparent text-white/70 hover:text-white'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Users className="text-purple-600" size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_students || 0}</p>
                    <p className="text-xs text-gray-500">Students</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Trophy className="text-amber-600" size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">#{stats.national_rank || '-'}</p>
                    <p className="text-xs text-gray-500">National Rank</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <Star className="text-cyan-600" size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stats.avg_score || 0}</p>
                    <p className="text-xs text-gray-500">Avg Score</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Code className="text-green-600" size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_problems_solved || 0}</p>
                    <p className="text-xs text-gray-500">Solved</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Target className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stats.contest_participations || 0}</p>
                    <p className="text-xs text-gray-500">Contests</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Zap className="text-orange-600" size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stats.challenge_participations || 0}</p>
                    <p className="text-xs text-gray-500">Challenges</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Performers & Skills */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Top Performers */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Medal className="text-amber-500" size={20} />
                  Top Performers
                </h2>
                {dashboard?.top_performers?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.top_performers.slice(0, 5).map((student, idx) => (
                      <div key={student.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg">
                        {getRankBadge(idx + 1)}
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{student.full_name}</p>
                          <p className="text-xs text-gray-500">{student.branch || 'Student'}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-purple-600">{student.total_score || 0}</p>
                          <p className="text-xs text-gray-500">points</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No students registered yet</p>
                )}
                <button
                  onClick={() => setActiveTab('leaderboard')}
                  className="w-full mt-4 text-center text-purple-600 text-sm hover:underline"
                >
                  View Full Leaderboard →
                </button>
              </div>

              {/* Skills Distribution */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Activity className="text-cyan-500" size={20} />
                  Top Skills
                </h2>
                {dashboard?.skill_distribution?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.skill_distribution.map((skill, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium text-gray-700">{skill.skill}</span>
                            <span className="text-gray-500">{skill.count} students</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"
                              style={{ width: `${Math.min(100, (skill.count / (stats.total_students || 1)) * 100)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No skill data available</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <div className="space-y-4">
            {/* Search */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search students by name, email, or username..."
                  value={studentSearch}
                  onChange={(e) => { setStudentSearch(e.target.value); setStudentsPage(1); }}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>

            {/* Students List */}
            {students.length > 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Branch</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Year</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Profile</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {students.map(student => (
                      <tr key={student.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-900">{student.full_name}</p>
                            <p className="text-sm text-gray-500">{student.email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{student.branch || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{student.year || '-'}</td>
                        <td className="px-6 py-4">
                          <span className="font-bold text-purple-600">{student.total_score || 0}</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {student.rank ? `#${student.rank}` : '-'}
                        </td>
                        <td className="px-6 py-4">
                          {student.username ? (
                            <Link
                              to={`/codearena/profile/${student.username}`}
                              target="_blank"
                              className="text-purple-600 hover:underline text-sm"
                            >
                              View →
                            </Link>
                          ) : (
                            <span className="text-gray-400 text-sm">No profile</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Pagination */}
                {totalStudents > 20 && (
                  <div className="flex justify-center gap-2 p-4 border-t border-gray-100">
                    <button
                      onClick={() => setStudentsPage(p => Math.max(1, p - 1))}
                      disabled={studentsPage === 1}
                      className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2 text-gray-600">
                      Page {studentsPage} of {Math.ceil(totalStudents / 20)}
                    </span>
                    <button
                      onClick={() => setStudentsPage(p => p + 1)}
                      disabled={studentsPage >= Math.ceil(totalStudents / 20)}
                      className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <Users className="mx-auto text-gray-300 mb-4" size={48} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Students Found</h3>
                <p className="text-gray-500">
                  {studentSearch ? 'Try a different search term' : 'Students will appear here when they register with your college name'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="font-bold text-gray-900 flex items-center gap-2">
                <Trophy className="text-amber-500" size={20} />
                College Leaderboard
              </h2>
            </div>
            
            {leaderboard.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {leaderboard.map((student) => (
                  <div key={student.id} className="px-6 py-4 flex items-center gap-4 hover:bg-gray-50">
                    {getRankBadge(student.college_rank)}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{student.full_name}</p>
                      <p className="text-sm text-gray-500">{student.branch || 'Student'} • {student.year || ''}</p>
                    </div>
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {student.skills?.slice(0, 3).map((skill, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-purple-50 text-purple-600 text-xs rounded">
                          {skill}
                        </span>
                      ))}
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-purple-600">{student.total_score || 0}</p>
                      <p className="text-xs text-gray-500">National #{student.rank || '-'}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <Trophy className="mx-auto text-gray-300 mb-4" size={48} />
                <p className="text-gray-500">No students in leaderboard yet</p>
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-6">
            {/* Score Distribution */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-bold text-gray-900 mb-4">Score Distribution</h2>
              <div className="grid grid-cols-5 gap-4">
                {analytics.score_distribution?.map((bucket, idx) => (
                  <div key={idx} className="text-center p-4 bg-gray-50 rounded-xl">
                    <p className="text-2xl font-bold text-purple-600">{bucket.count}</p>
                    <p className="text-sm text-gray-500">{bucket.range}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Year & Branch Distribution */}
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="font-bold text-gray-900 mb-4">Year-wise Distribution</h2>
                <div className="space-y-3">
                  {analytics.year_distribution?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="font-medium">{item.year}</span>
                      <div className="text-right">
                        <span className="text-purple-600 font-bold">{item.count}</span>
                        <span className="text-gray-400 text-sm ml-2">(avg: {item.avg_score})</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="font-bold text-gray-900 mb-4">Branch-wise Distribution</h2>
                <div className="space-y-3">
                  {analytics.branch_distribution?.slice(0, 6).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="font-medium truncate max-w-[200px]">{item.branch}</span>
                      <div className="text-right">
                        <span className="text-purple-600 font-bold">{item.count}</span>
                        <span className="text-gray-400 text-sm ml-2">(avg: {item.avg_score})</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Placement Stats */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Briefcase className="text-green-500" size={20} />
                Placement Stats
              </h2>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <p className="text-3xl font-bold text-blue-600">{analytics.placement_stats?.applications || 0}</p>
                  <p className="text-sm text-gray-600">Applications Sent</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-xl">
                  <p className="text-3xl font-bold text-green-600">{analytics.placement_stats?.hired || 0}</p>
                  <p className="text-sm text-gray-600">Hired</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-xl">
                  <p className="text-3xl font-bold text-purple-600">{analytics.placement_stats?.conversion_rate || 0}%</p>
                  <p className="text-sm text-gray-600">Conversion Rate</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CollegeDashboardPage;
